import React, { useMemo, useEffect, useState } from 'react';
import { Grid, Box, Typography, Card, Badge, Chip } from '@mui/material';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import './CallCenterWallboard.css';
import FilterDrawerCaseWise from '../../FilterDrawer/FilterDrawerCaseWise';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import { getWallboardCallMetric, getWallboardDashboardMetric } from '../../../services/api-service';
import { secondsToMinSec } from '../../../utils/util';

interface KPICardProps {
    title: string;
    value: string | number;
    backgroundColor: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, backgroundColor, }) => (
    <Card className="kpi-card" sx={{ backgroundColor }}>
        <Typography className="kpi-title" sx={{ color: "#FFFFFF" }}>
            {title}
        </Typography>
        <Typography className="kpi-value" sx={{ color: "#212121" }}>
            {value}
        </Typography>
    </Card>
);

// Function to get Total Queue color
const getTotalQueueColor = (value: number): { backgroundColor: string; } => {
    if (value > 1) {
        return { backgroundColor: '#ff0000' }; // Red
    } else if (value >= 0 && value <= 1) {
        return { backgroundColor: '#FFA500' }; // Orange
    } else {
        return { backgroundColor: '#3bb0f0' }; // Default Blue
    }
};

interface AgentDashboardMetricInterface {
    "AbandonedPercentagePerDay": number | string,
    "AbandonedPercentagePerInterval": number | string,
    "AHTPerDay": number | string,
    "AHTPerInterval": number | string,
    "SLAPercentagePerDay": number | string,
    "SLAPercentagePerInterval": number | string,
    "SLAPerDay": number | string,
    "SLAPerInterval": number | string,
    "TotalAbandonedPerDay": number | string,
    "TotalAbandonedPerInterval": number | string,
    "TotalCallsPerDay": number | string,
    "TotalCallsPerInterval": number | string,
}

interface AgentDataInterface {
    IdleCall: number | string,
    InBreak: number | string,
    LoggedIn: number | string,
    LongCall: number | string,
    OnCall: number | string,
    OnHold: number | string,
}

const RightPanelCard = ({ name, value, backgroundColor }: { name: string, value: number, backgroundColor: string }) => {
    return (
        <Grid item xs={12} sx={{
            backgroundColor: backgroundColor,
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 1,
            height: '20%',
            minHeight: "55px"
        }}>
            <Typography className="kpi-title" sx={{ color: "#FFFFFF" }}>
                {name}
            </Typography>
            <Typography className="kpi-value" sx={{ color: "#212121" }}>
                {value}
            </Typography>
        </Grid>
    );
}

const DefaultDashboardMetric = {
    TotalCallsPerDay: '',
    SLAPerDay: '',
    SLAPercentagePerDay: '',
    TotalCallsPerInterval: '',
    SLAPerInterval: '',
    SLAPercentagePerInterval: '',
    AHTPerDay: '',
    TotalAbandonedPerDay: '',
    AbandonedPercentagePerDay: '',
    AHTPerInterval: '',
    TotalAbandonedPerInterval: '',
    AbandonedPercentagePerInterval: '',
}

const DefaultAgentData = {
    LoggedIn: '',
    InBreak: '',
    LongCall: '',
    OnCall: '',
    OnHold: '',
    IdleCall: '',
}

const DefaultEmptyDashboardMetric = {
    TotalCallsPerDay: '0',
    SLAPerDay: '0',
    SLAPercentagePerDay: '0',
    TotalCallsPerInterval: '0',
    SLAPerInterval: '0',
    SLAPercentagePerInterval: '0',
    AHTPerDay: '0',
    TotalAbandonedPerDay: '0',
    AbandonedPercentagePerDay: '0',
    AHTPerInterval: '0',
    TotalAbandonedPerInterval: '0',
    AbandonedPercentagePerInterval: '0',
}

const DefaultEmptyAgentData = {
    LoggedIn: '0',
    InBreak: '0',
    LongCall: '0',
    OnCall: '0',
    OnHold: '0',
    IdleCall: '0',
}


let isWidgetDataLoading = false;

const CallCenterWallboard: React.FC = (props: any) => {

    const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
    const [caseFilters, setCaseFilters] = useState<any>(props?.widgetData?.caseFilters || { caseCondition: 'AND', caseFilters: [{ condition: 'AND', conditionFilters: [{ filterType: '', filterSection: null, selectedFilters: [] }] }] });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Sample data - in real app this would come from API
    const [agentDashboardMetric, setAgentDashboardMetric] = useState<AgentDashboardMetricInterface>(DefaultDashboardMetric);

    const [agentData, setAgentData] = useState<AgentDataInterface>(DefaultAgentData);
    const [callData, setCallData] = useState<any>([]);
    const [top3QueueDataBySkillName, setTop3QueueDataBySkillName] = useState<any>([]);

    // useEffect(() => {
    //     if (props?.widgetData?.caseFilters) {
    //         setCaseFilters(props?.widgetData?.caseFilters);
    //     }
    // }, [props])

    const selectedCaseFiltersItems = useMemo(() => {
        let items: any = [];
        try {
            if (caseFilters?.caseFilters?.length > 0 && Array.isArray(caseFilters?.caseFilters)) {
                caseFilters.caseFilters.forEach((caseFilter: any) => {
                    if (caseFilter?.conditionFilters?.length > 0 && Array.isArray(caseFilter?.conditionFilters)) {
                        caseFilter?.conditionFilters?.forEach((condition: any) => {
                            if (condition?.selectedFilters && Array.isArray(condition?.selectedFilters)) {
                                let filterLabel = condition?.filterSection?.label;
                                condition?.selectedFilters?.forEach((filter: any) => {
                                    items.push(filterLabel + " - " + filter?.label);
                                });
                            }
                        });
                    }
                });
            }
            if (items?.length > 0) {
                items = [...new Set(items)];
            }
            return items;
        } catch (error) {
            console.log("error in selectedCaseFiltersItems", error)
            return [];
        }
    }, [caseFilters]);

    const caseFiltersLength = useMemo(() => {
        try {
            return selectedCaseFiltersItems?.length || 0;
        } catch (error) {
            console.log("error in caseFiltersLength", error)
            return 0;
        }
    }, [selectedCaseFiltersItems]);

    const caseFilterChipItems = useMemo(() => {
        return (selectedCaseFiltersItems?.length > 0 && Array.isArray(selectedCaseFiltersItems)) ? selectedCaseFiltersItems?.map((item: any, index: number) => (
            item ? <Chip key={index + item} label={item} color="primary" variant="outlined" size="small" /> : null
        )) : null;
    }, [selectedCaseFiltersItems]);

    // Function to determine card color based on threshold rules
    const getCardColor = (title: string, value: number | string): { backgroundColor: string; } => {
        const numValue = typeof value === 'string' ? parseFloat(value.toString().replace('%', '')) : value;
        console.log("numValue", numValue, title)

        switch (title) {
            case 'SLA % - Day':
            case 'SLA % - Interval':
                if (numValue < 80) {
                    return { backgroundColor: '#ff0000' }; // Red
                } else if (numValue >= 80) {
                    return { backgroundColor: '#3bb0f0' }; // Blue
                } else {
                    return { backgroundColor: '#FFA500' }; // Orange
                }

            case 'Abandoned % - Day':
            case 'Abandoned % - Interval':
                if (numValue > 5) {
                    return { backgroundColor: '#ff0000' }; // Red
                } else if (numValue <= 5) {
                    return { backgroundColor: '#3bb0f0' }; // Blue
                } else {
                    return { backgroundColor: '#FFA500' }; // Orange
                }

            case 'Long Call':
                if (numValue > 0) {
                    return { backgroundColor: '#ff0000' }; // Red
                } else if (numValue <= 0) {
                    return { backgroundColor: '#3bb0f0' }; // Blue
                } else {
                    return { backgroundColor: '#FFA500' }; // Orange
                }

            default:
                return { backgroundColor: '#3bb0f0' }; // Default Blue
        }
    };



    const toggleDrawer = (isOpen: boolean) => {
        setIsDrawerOpen((isOpen));
    };

    function handleApplyCaseFilters(filters: any) {
        console.log("handleApplyCaseFilters filters", filters)
        setCaseFilters(filters);
        toggleDrawer(false);
        if (props && props?.handleSaveDataToFilter) {
            props?.handleSaveDataToFilter({
                widgetId: props?.widgetId,
                value: {
                    caseFilters: filters
                }
            })
        }
    }


    function transformCaseFiltersToApiParams(caseFiltersData: any) {
        try {
            if (!caseFiltersData || !caseFiltersData.caseFilters || !Array.isArray(caseFiltersData.caseFilters)) {
                return "0"; // Or return a default structure / throw error
            }

            const apiConditions = caseFiltersData.caseFilters.map((caseFilter: any, index: number) => {
                const conditionFilters = caseFilter?.conditionFilters?.map((condition: any) => {
                    // Ensure filter.id exists and is a string before parsing
                    let pFilterId = condition?.selectedFilters
                        ?.map((filter: any) => (filter && typeof filter.id === 'string' ? filter.id : NaN))
                    // .filter((id: number) => !isNaN(id));

                    // Ensure filterType exists and is a string before parsing
                    const pType = condition && typeof condition?.filterType === 'string' ? condition?.filterType : NaN;

                    // Only return if pType is a valid number and pFilterId is not empty
                    if (pFilterId && pFilterId?.length > 0) {
                        try {
                            if (Array.isArray(pFilterId)) {
                                pFilterId = pFilterId.map((item: any) => {
                                    if (item) {
                                        if (typeof item === 'string' && item.split(',').length > 1) {
                                            return item.split(',').map((item: any) => item);
                                        }
                                        return item;
                                    }
                                    return null;
                                }).filter((item: any) => item !== null).flat(3);
                            }
                        } catch (e) {
                            console.log("error in transformCaseFiltersToApiParams", e)
                        }
                        return {
                            pType: pType,
                            pFilterId: pFilterId,
                        };
                    }
                    return null; // Return null for invalid conditions
                }).filter((cf: any) => cf !== null); // Filter out null (invalid) conditions

                let condition: any = {};
                // if (conditionFilters?.length > 1) {
                condition.operator = caseFilter?.condition || "AND";
                // }
                return {
                    ...condition,
                    conditions: conditionFilters || [],
                    name: `case ${index + 1}`,
                };
            }).filter((c: any) => c?.conditions?.length > 0); // Ensure the case has valid conditions

            // The top-level operator seems to combine the different cases.
            // Based on the API example structure, it looks like the cases are OR'd together.
            // The caseFiltersData.caseCondition ("AND" in the example) applies *within* a case.
            if (apiConditions?.length === 0) {
                return "0";
            }
            let filter: any = {};
            // if (apiConditions?.length > 1) {
            //     filter.operator = caseFiltersData?.caseCondition || "AND";
            // }
            // if (apiConditions?.length === 1 && apiConditions[0]?.conditions?.length === 1) {
            //     return {
            //         filter: apiConditions[0]
            //     };
            // }
            return {
                filter: {
                    ...filter,
                    operator: caseFiltersData?.caseCondition,
                    conditions: apiConditions,
                },
            };
        } catch (e) {
            console.log("error in transformCaseFiltersToApiParams", e)
            return "0";
        }
    }

    async function getWidgetData(caseFilters: any) {
        // If already loading, don't start a new request
        if (isWidgetDataLoading) {
            console.log('Widget data fetch already in progress, skipping this cycle');
            return;
        }
        try {
            // Set loading flag to true
            isWidgetDataLoading = true;

            let filter: any = '0';
            try {
                if (caseFilters?.caseFilters?.length > 0 && Array.isArray(caseFilters?.caseFilters)) {
                    filter = transformCaseFiltersToApiParams(caseFilters);
                }
            } catch (e) {
                console.log("error in getWidgetData", e)
            }
            console.log("filter", structuredClone(filter))
            if (typeof filter === 'object') {
                filter = JSON.stringify(filter);
            }
            // Make all API calls in parallel
            await Promise.all([
                getWallboardMetric("Agent", filter),
                getWallboardMetric("Call", filter),
                getDashboardMetric(filter)
            ]);
            console.log('All widget data fetched successfully');
        } catch (error) {
            console.error('Error fetching widget data:', error);
        } finally {
            // Reset loading flag regardless of success or failure
            isWidgetDataLoading = false;
            setLastFetchedTime(getLastFetchedDetailFromBrowser());
        }
    };

    useEffect(() => {
        getWidgetData(caseFilters);
        const interval: any = setInterval(() => {
            getWidgetData(caseFilters);
        }, WIDGET_REFRESH_INTERVAL.CALL_CENTER_WALLBOARD);
        return () => clearInterval(interval);
    }, [caseFilters]);

    function convertDurationToSeconds(duration: string) {
        try {
            return duration.split(':').reduce((acc: any, curr: any, index: number) => acc + curr * (index === 0 ? 3600 : index === 1 ? 60 : 1), 0)
        } catch (error) {
            console.log("error in convertDurationToSeconds", error)
            return 0;
        }
    }

    async function getWallboardMetric(type: "Agent" | "Call", caseFilters: any) {
        try {
            let resp = await getWallboardCallMetric({ Type: type, Filter: caseFilters });
            if (resp && Array.isArray(resp)) {
                if (type === "Agent") {
                    const data = resp[0];
                    if (data && typeof data === 'object') {
                        setAgentData((prev: AgentDataInterface) => ({
                            ...prev,
                            LoggedIn: data.LoggedIn,
                            InBreak: data.InBreak,
                            LongCall: data.LongCall,
                            OnCall: data.OnCall,
                            OnHold: data.OnHold,
                            IdleCall: data.IdleCall
                        }))
                    } else {
                        setAgentData((prev: AgentDataInterface) => ({
                            ...prev,
                            ...DefaultEmptyAgentData
                        }))
                    }
                } else if (type === "Call") {
                    if (resp.length > 0) {
                        const skillNameCount: any = {};
                        const callQueueData = resp.map((item: any) => {
                            if (skillNameCount[item.SkillName]) {
                                skillNameCount[item.SkillName] = skillNameCount[item.SkillName] + 1;
                            } else {
                                skillNameCount[item.SkillName] = 1;
                            }
                            return {
                                skillName: item.SkillName,
                                duration: item.Duration,
                                durationInSeconds: convertDurationToSeconds(item.Duration)
                            }
                        });
                        let top3QueueDataBySkillNameWithCount = Object.keys(skillNameCount).map((item: any) => ({
                            skillName: item,
                            count: skillNameCount[item]
                        })).sort((a: any, b: any) => b.count - a.count).slice(0, 3);
                        setCallData(callQueueData.sort((a: any, b: any) => b.durationInSeconds - a.durationInSeconds));
                        setTop3QueueDataBySkillName(top3QueueDataBySkillNameWithCount);
                    } else {
                        setCallData([]);
                        setTop3QueueDataBySkillName([]);
                    }
                }
            } else {
                if (type === "Agent") {
                    setAgentData((prev: AgentDataInterface) => ({
                        ...prev,
                        ...DefaultAgentData
                    }))
                } else {
                    setCallData([]);
                    setTop3QueueDataBySkillName([]);
                }
            }
        } catch (error) {
            console.log("error in getWallboardMetric", error)
            if (type === "Agent") {
                setAgentData((prev: AgentDataInterface) => ({
                    ...prev,
                    ...DefaultAgentData
                }))
            } else {
                setCallData([]);
                setTop3QueueDataBySkillName([]);
            }
        }
    }

    function convertSecondsToTime(seconds: string) {
        try {
            return secondsToMinSec(Number(Number(seconds).toFixed(0)))
        } catch (error) {
            console.log("error in convertSecondsToTime", error)
            return '';
        }
    }

    async function getDashboardMetric(caseFilters: any) {
        try {
            const resp = await getWallboardDashboardMetric({ CustomTagFilter: caseFilters });
            if (resp && Array.isArray(resp)) {
                const data = resp[0];
                if (data && typeof data === 'object') {
                    setAgentDashboardMetric((prev: any) => ({
                        ...prev,
                        TotalCallsPerDay: data.TotalCallsPerDay || '',
                        SLAPerDay: data.SLAPerDay || '',
                        SLAPercentagePerDay: data.SLAPercentagePerDay || '',
                        TotalCallsPerInterval: data.TotalCallsPerInterval || '',
                        SLAPerInterval: data.SLAPerInterval || '',
                        SLAPercentagePerInterval: data.SLAPercentagePerInterval || '',
                        AHTPerDay: data.AHTPerDay ? convertSecondsToTime(data.AHTPerDay) : '',
                        TotalAbandonedPerDay: data.TotalAbandonedPerDay || '',
                        AbandonedPercentagePerDay: data.AbandonedPercentagePerDay || '',
                        AHTPerInterval: data.AHTPerInterval ? convertSecondsToTime(data.AHTPerInterval) : '',
                        TotalAbandonedPerInterval: data.TotalAbandonedPerInterval || '',
                        AbandonedPercentagePerInterval: data.AbandonedPercentagePerInterval || '',
                    }))
                } else {
                    setAgentDashboardMetric((prev: any) => ({
                        ...prev,
                        ...DefaultEmptyDashboardMetric
                    }))
                }
            } else {
                setAgentDashboardMetric((prev: any) => ({
                    ...prev,
                    ...DefaultDashboardMetric
                }))
            }
        } catch (error) {
            console.log("error in getDashboardMetric", error)
            setAgentDashboardMetric((prev: any) => ({
                ...prev,
                ...DefaultDashboardMetric
            }))
        }
    }

    return (
        <Card className="customCard">
            <div className="card-header">
                <div className="card-title">
                    Wallboard <div className="card-title-badge">Live</div>
                    <span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
                </div>
                <div className="card-actions">
                    {/* <WidgetDownload element={downloadContent} handleDownload={handleDownload} xlsx rowData={getRowData(rowData)} columns={columnData} name={'Queue Counter'}></WidgetDownload> */}
                    <div className="card-icon" onClick={() => toggleDrawer(true)}>
                        <Badge badgeContent={caseFiltersLength} color="primary">
                            <FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
                        </Badge>
                    </div>
                </div>
            </div>

            <FilterDrawerCaseWise
                isOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                applyFilters={handleApplyCaseFilters}
                existingCaseFilters={caseFilters}
                allowedFilters={[]}
                dynamicFilters={true}
                useTagFilters={true}
            />
            <Box className="wallboard-container-call-center">
                {caseFiltersLength > 0 &&
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        gap: '4px',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        pb: 1
                    }}>
                        {caseFilterChipItems}
                    </Box>
                }
                {/* Main Dashboard Grid */}
                <Grid container spacing={2} className="call-center-dashboard-grid">
                    {/* Left Section - Main KPIs */}
                    <Grid item sm={12} md={9}>
                        <Grid container spacing={2}>
                            {/* Row 1 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Total Calls - Day"
                                    value={agentDashboardMetric.TotalCallsPerDay}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="SLA - 20 Sec - Day"
                                    value={agentDashboardMetric.SLAPerDay}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                {(() => {
                                    const colors = getCardColor('SLA % - Day', agentDashboardMetric.SLAPercentagePerDay);
                                    return (
                                        <KPICard
                                            title="SLA % - Day"
                                            value={agentDashboardMetric.SLAPercentagePerDay ? `${agentDashboardMetric.SLAPercentagePerDay}%` : ''}
                                            backgroundColor={colors.backgroundColor}
                                        />
                                    );
                                })()}
                            </Grid>

                            {/* Row 2 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Calls - Interval"
                                    value={agentDashboardMetric.TotalCallsPerInterval}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="SLA -20 Sec - Interval"
                                    value={agentDashboardMetric.SLAPerInterval}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                {(() => {
                                    const colors = getCardColor('SLA % - Interval', agentDashboardMetric.SLAPercentagePerInterval);
                                    return (
                                        <KPICard
                                            title="SLA % - Interval"
                                            value={agentDashboardMetric.SLAPercentagePerInterval ? `${agentDashboardMetric.SLAPercentagePerInterval}%` : ''}
                                            backgroundColor={colors.backgroundColor}
                                        />
                                    );
                                })()}
                            </Grid>

                            {/* Row 3 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="AHT - DAY"
                                    value={agentDashboardMetric.AHTPerDay}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Abandoned - Day"
                                    value={agentDashboardMetric.TotalAbandonedPerDay}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                {(() => {
                                    const colors = getCardColor('Abandoned % - Day', agentDashboardMetric.AbandonedPercentagePerDay);
                                    return (
                                        <KPICard
                                            title="Abandoned % - Day"
                                            value={agentDashboardMetric.AbandonedPercentagePerDay ? `${agentDashboardMetric.AbandonedPercentagePerDay}%` : ''}
                                            backgroundColor={colors.backgroundColor}
                                        />
                                    );
                                })()}
                            </Grid>

                            {/* Row 4 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="AHT - Interval"
                                    value={agentDashboardMetric.AHTPerInterval}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Abandoned - Interval"
                                    value={agentDashboardMetric.TotalAbandonedPerInterval}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                {(() => {
                                    const colors = getCardColor('Abandoned % - Interval', agentDashboardMetric.AbandonedPercentagePerInterval);
                                    return (
                                        <KPICard
                                            title="Abandoned % - Interval"
                                            value={agentDashboardMetric.AbandonedPercentagePerInterval ? `${agentDashboardMetric.AbandonedPercentagePerInterval}%` : ''}
                                            backgroundColor={colors.backgroundColor}
                                        />
                                    );
                                })()}
                            </Grid>

                            {/* Row 5 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Logged In"
                                    value={agentData.LoggedIn}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="In Break"
                                    value={agentData.InBreak}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                {(() => {
                                    const colors = getCardColor('Long Call', agentData.LongCall);
                                    return (
                                        <KPICard
                                            title="Long Call"
                                            value={agentData.LongCall}
                                            backgroundColor={colors.backgroundColor}
                                        />
                                    );
                                })()}
                            </Grid>

                            {/* Row 6 */}
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="On Call"
                                    value={agentData.OnCall}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="On Hold"
                                    value={agentData.OnHold}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={4}>
                                <KPICard
                                    title="Idle Call"
                                    value={agentData.IdleCall}
                                    backgroundColor="#3bb0f0"
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Section - Waiting Calls and Queue Counters */}
                    <Grid item sm={12} md={3} sx={{ height: '100%' }}>
                        <Grid container spacing={2} sx={{ height: '100%', gap: 1 }}>
                            {/* Waiting Calls */}
                            <Grid item xs={12} sx={{ height: '49%', mt: 1 }}>
                                <Box className="waiting-calls-header">
                                    <Typography className="queue-header" sx={{ width: '100% !important' }}>
                                        Waiting Calls
                                    </Typography>
                                </Box>
                                <Box className="waiting-calls-list">
                                    <Box className="waiting-calls-header">
                                        <Typography className="queue-header">Queue Name</Typography>
                                        <Typography className="duration-header">Duration</Typography>
                                    </Box>
                                    {callData.length > 0 && callData.map((call: any, index: number) => (
                                        <Box key={index} className="waiting-call-item">
                                            <Typography className="queue-name">
                                                {call.skillName}
                                            </Typography>
                                            <Typography className="queue-duration">
                                                {call.duration}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sx={{ height: '49%', mt: 1 }}>
                                <Grid container spacing={1} columnGap={1} gap={1} height={"100%"} maxHeight={"100%"} alignContent={"flex-start"}>
                                    <RightPanelCard name="Total Queue" value={callData?.length || ''} backgroundColor={getTotalQueueColor(callData?.length || -1).backgroundColor} />
                                    {
                                        top3QueueDataBySkillName.map((item: any) => (
                                            <RightPanelCard name={item.skillName} value={item.count} backgroundColor={getTotalQueueColor(item.count || -1).backgroundColor} />
                                        ))
                                    }

                                    {/* <RightPanelCard name="Tamil_Others" value={queueCounters['Tamil_Others']} backgroundColor="#3bb0f0" />
                                    <RightPanelCard name="Hindi_Intimation" value={queueCounters['Hindi_Intimation']} backgroundColor="#3bb0f0" />
                                    <RightPanelCard name="Malayalam_Others" value={queueCounters['Malayalam_Others']} backgroundColor="#3bb0f0" /> */}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid >
            </Box >
        </Card >
    );
};

export default CallCenterWallboard; 